// BusinessProof.jsx
import React from "react";
import { useFormik } from "formik";
import { InputBox } from "../CustomWidgets/InputBox";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import UploadBusinessMedia from "../uploadBuinessProofs";
import LoadingSpinner from "../Loading";

function BusinessProof({ onDataSubmit, savedata }) {
  const { t } = useTranslation();
  const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
  const [previewImage, setPreviewImage] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);

  // Prefix mapping (as confirmed)
  const PREFIXES = {
    commercialregistrynumberurl: "commercial_registry_certificate",
    taxcardnumberurl: "tax_card",
    manageridurl: "manageridurl",
  };

  // normalize savedata.entry.url (string | array) -> array of strings
  const normUrlArray = (entry) => {
    if (!entry) return [];
    const url = entry.url;
    if (!url) return [];
    if (Array.isArray(url)) return url;
    if (typeof url === "string" && url.trim() !== "") return [url];
    return [];
  };

  // map saved urls -> file objects used in formik
  const mapSavedToFileObjs = (entry) => {
    const arr = normUrlArray(entry);
    return arr.map((u) => ({
      dataUrl: u,
      originalName: "", // unknown from saved
      size: 0,
      type: u?.startsWith?.("data:image")
        ? u.startsWith("data:image/png")
          ? "image/png"
          : "image/jpeg"
        : u.startsWith("data:application/pdf")
        ? "application/pdf"
        : "",
      uploadedUrl: !u.startsWith?.("data:") ? u : null, // if backend returned real url (not data:) keep it
    }));
  };

  // Local Yup schema: allows arrays for *url fields
  const schema = yup.object().shape({
    commercialregistrynumber: yup
      .string()
      .nullable()
      .test(
        "crn-format-or-empty",
        "Commercial Registry Number must be 14 digits",
        (val) => {
          if (!val) return true; // optional
          return /^\d{14}$/.test(val);
        }
      ),
    commercialregistrynumberurl: yup
      .array()
      .of(
        yup.object().shape({
          dataUrl: yup.string().required(),
          originalName: yup.string().nullable(),
          size: yup.number().nullable(),
          type: yup.string().nullable(),
        })
      )
      .nullable(),

    taxcardnumber: yup
      .string()
      .nullable()
      .test(
        "tax-format-or-empty",
        "Tax Card Number must be 9 digits",
        (val) => {
          if (!val) return true;
          return /^\d{9}$/.test(val);
        }
      ),
    taxcardnumberurl: yup
      .array()
      .of(
        yup.object().shape({
          dataUrl: yup.string().required(),
          originalName: yup.string().nullable(),
        })
      )
      .nullable(),

    managerid: yup
      .string()
      .required("Manager ID is required")
      .test("manager-format", "Manager ID must be 14 digits", (val) =>
        /^\d{14}$/.test(val)
      ),

   manageridurl: yup.array()
  .nullable()
  .test("manager-id-required", "Manager ID file is required", (value) => {
    return value && value.length > 0;
  }),

  });

  const formik = useFormik({
    initialValues: {
      commercialregistrynumber: savedata?.[0]?.name ?? "",
      commercialregistrynumberurl: mapSavedToFileObjs(savedata?.[0]),

      taxcardnumber: savedata?.[1]?.name ?? "",
      taxcardnumberurl: mapSavedToFileObjs(savedata?.[1]),

      managerid: savedata?.[2]?.name ?? "",
      manageridurl: mapSavedToFileObjs(savedata?.[2]),
    },
    validationSchema: schema,
    validateOnChange: true,
    onSubmit: async (values) => {
      setUploading(true);
      try {
        // upload files one-by-one (Option A) with auto-indexed filenames
        const uploadSingleFile = async (fieldPrefix, fileObj, idx) => {
          // If already uploaded to server (uploadedUrl exists and is not a data:), skip
          if (
            fileObj?.uploadedUrl &&
            typeof fileObj.uploadedUrl === "string" &&
            !fileObj.uploadedUrl.startsWith("data:")
          ) {
            return fileObj.uploadedUrl;
          }

          // const filename = `${fieldPrefix}_${idx + 1}`;
          const filename = `${fieldPrefix}`;


          const payload = {
            user_id,
            filename,
            // If your backend expects only base64 (without prefix), use fileObj.dataUrl.split(',')[1]
            attachment: fileObj.dataUrl,
          };

          try {
            const res = await axiosInstance.post(URLS.UPLOAD_RETAILER_DOCS, payload);

            // extract server URL (adapt if your API differs)
            const serverUrl =
              res?.data?.result?.url ||
              res?.data?.result?.data ||
              res?.data?.url ||
              res?.data?.result?.attachmentUrl ||
              null;

            return serverUrl || fileObj.dataUrl;
          } catch (err) {
            console.error("Upload error", filename, err);
            // on failure, fallback to local dataUrl so UI doesn't break
            return fileObj.dataUrl;
          }
        };

        const fields = [
          "commercialregistrynumberurl",
          "taxcardnumberurl",
          "manageridurl",
        ];

        const resultUrls = {
          commercialregistrynumberurl: [],
          taxcardnumberurl: [],
          manageridurl: [],
        };

        for (const field of fields) {
          const files = values[field] ?? [];
          const prefix = PREFIXES[field];
          for (let i = 0; i < files.length; i++) {
            const fileObj = files[i];
            const uploaded = await uploadSingleFile(prefix, fileObj, i);
            resultUrls[field].push(uploaded);
          }
        }

        const bussiness_proof = [
          {
            name: values.commercialregistrynumber ?? "",
            url: resultUrls.commercialregistrynumberurl,
          },
          {
            name: values.taxcardnumber ?? "",
            url: resultUrls.taxcardnumberurl,
          },
          {
            name: values.managerid ?? "",
            url: resultUrls.manageridurl,
          },
        ];

        const api_payload = {
          user_id,
          manager_id: values.managerid,
          tax_card_number: values.taxcardnumber,
          company_registry: values.commercialregistrynumber,
          bussiness_proof,
        };

        onDataSubmit("businessProofData", api_payload);
      } finally {
        setUploading(false);
      }
    },
  });

  // safe error renderer: string, array, or object
  const renderError = (err) => {
    if (!err) return null;
    if (Array.isArray(err)) return err.join(", ");
    if (typeof err === "object") {
      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    }
    return String(err);
  };

  console.log("formik.errors===>",formik.errors);
  

  // handle file object from UploadBusinessMedia
  // We expect: { name: "<fieldName>", dataUrl: "...", originalName, size, type }
const handleurlgenerated = (fileMeta) => {
  if (!fileMeta || !fileMeta.name) return;
  const fieldName = fileMeta.name;

  const fileObj = {
    dataUrl: fileMeta.dataUrl,
    originalName: fileMeta.originalName || fileMeta.fileName || "",
    size: fileMeta.size || 0,
    type: fileMeta.type || "",
    uploadedUrl: null,
  };

  const existing = formik.values[fieldName] ?? [];
  const updated = [...existing, fileObj];
  formik.setFieldValue(fieldName, updated);

  // ⭐ IMPORTANT: Clear managerid error when file uploaded
  if (fieldName === "manageridurl") {
    formik.setFieldError("managerid", undefined);
    formik.setTouched({ ...formik.touched, managerid: false });
  }
};


  const handleRemoveFile = (fieldName, index) => {
    const arr = formik.values[fieldName] ?? [];
    const updated = arr.filter((_, i) => i !== index);
    formik.setFieldValue(fieldName, updated);
  };

  // viewers
  const handleViewPDF = (base64) => {
    if (!base64) return;
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill()
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (err) {
      console.error("Error opening PDF", err);
    }
  };
  const handleViewImage = (fullBase64Url) => setPreviewImage(fullBase64Url);

  const isPdf = (fileObj) =>
    typeof fileObj?.dataUrl === "string" && fileObj.dataUrl.startsWith("data:application/pdf");
  const isJpeg = (fileObj) =>
    typeof fileObj?.dataUrl === "string" && fileObj.dataUrl.startsWith("data:image/jpeg");
  const isPng = (fileObj) =>
    typeof fileObj?.dataUrl === "string" && fileObj.dataUrl.startsWith("data:image/png");
  const isImage = (fileObj) => isJpeg(fileObj) || isPng(fileObj);

  // Next button disable logic:
  const anyFilesWithoutName =
    (formik.values.commercialregistrynumberurl?.length > 0 &&
      (formik.values.commercialregistrynumber ?? "").trim() === "") ||
    (formik.values.taxcardnumberurl?.length > 0 &&
      (formik.values.taxcardnumber ?? "").trim() === "") ||
    (formik.values.manageridurl?.length > 0 &&
      (formik.values.managerid ?? "").trim() === "");


const hasError = (err) => {
  if (!err) return false;
  if (typeof err === "string") return true;
  if (Array.isArray(err)) return err.length > 0;
  return false;
};

const isDisabled =
  uploading ||
  anyFilesWithoutName ||
  hasError(formik.errors.managerid);

  // render file list safely (no object printed)
  const renderFileList = (fieldName, filesArray) => {
    if (!filesArray || filesArray.length === 0) return null;
    return filesArray.map((fileObj, idx) => {
      const label = fileObj?.originalName || `File ${idx + 1}`;
      return (
        <div key={idx} className="flex items-center gap-3 mt-2">
          {isImage(fileObj) ? (
            <img
              src={fileObj.dataUrl}
              alt={`thumb-${idx}`}
              className="h-10 w-10 object-cover rounded-md border"
            />
          ) : (
            <div className="h-10 w-10 flex items-center justify-center rounded-md border text-xs">PDF</div>
          )}

          <button
            type="button"
            onClick={() => {
              if (isPdf(fileObj)) {
                const base64 = fileObj.dataUrl.split(",")[1];
                handleViewPDF(base64);
              } else if (isImage(fileObj)) {
                handleViewImage(fileObj.dataUrl);
              } else {
                window.open(fileObj.dataUrl, "_blank");
              }
            }}
            className="text-blue-500 underline text-sm"
          >
            {t("View File")} - {label}
          </button>

          <button
            type="button"
            onClick={() => handleRemoveFile(fieldName, idx)}
            className="text-red-500 text-xs ml-2"
            aria-label={`Remove file ${idx + 1}`}
          >
            {t("Remove")}
          </button>
        </div>
      );
    });
  };

  return (
    <div>
      <h1 className="text-xl font-bold">{t("business_proof")}</h1>

      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-2 mt--4 text-sm gap-x-7 pb-12">
          {/* Commercial Registry Number */}
          <div>
            <InputBox
              label={t("commercial_registry_number")}
              name="commercialregistrynumber"
              placeholder={t("enter_commercial_registry_number")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.commercialregistrynumber}
              error={
                (formik.values.commercialregistrynumberurl?.length > 0 &&
                  (formik.values.commercialregistrynumber ?? "").trim() === "") ||
                !!formik.errors.commercialregistrynumber
              }
              touched={
                (formik.values.commercialregistrynumberurl?.length > 0 &&
                  (formik.values.commercialregistrynumber ?? "").trim() === "") ||
                !!formik.touched.commercialregistrynumber
              }
            />
            {formik.errors.commercialregistrynumber && (
              <div className="text-red text-xs">{renderError(t(formik.errors.commercialregistrynumber))}</div>
            )}

            <div className="w-[363px] md:w-full mt-2">
              <div className="rounded-lg border border-dashed border-primaryBlue px-2 py-3">
                <div className="flex flex-col gap-y-2" style={{ minHeight: 70, maxHeight: 220 }}>
                  {renderFileList("commercialregistrynumberurl", formik.values.commercialregistrynumberurl)}

                  {formik.values.commercialregistrynumberurl?.length < 4 && (
  <div className={`flex flex-row text-sm leading-6 justify-center text-gray-600 ${formik.values.commercialregistrynumberurl?.length === 0 ? "w-full" : " w-8/12"}`}>
    <label
      htmlFor="file-upload-commercialregistrynumberurl"
      className={`flex items-center relative rounded-md font-semibold ${
        formik.values.commercialregistrynumberurl?.length >= 4
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-white text-primaryBlue cursor-pointer"
      }`}
    >
      <UploadBusinessMedia
        foldername={"store_app_UAT"}
        onfileAdd={"commercialregistrynumberurl"}
        urlgenerated={handleurlgenerated}
        multiple={true}
        message={() => false}
      />
    </label>
  </div>
)}
                </div>
              </div>
            </div>
          </div>

          {/* Tax Card Number */}
          <div>
            <InputBox
              label={t("taxcard_number")}
              name="taxcardnumber"
              placeholder={t("enter_taxcard_number")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.taxcardnumber}
              error={
                (formik.values.taxcardnumberurl?.length > 0 &&
                  (formik.values.taxcardnumber ?? "").trim() === "") ||
                !!formik.errors.taxcardnumber
              }
              touched={
                (formik.values.taxcardnumberurl?.length > 0 &&
                  (formik.values.taxcardnumber ?? "").trim() === "") ||
                !!formik.touched.taxcardnumber
              }
            />
            {formik.errors.taxcardnumber && (
              <div className="text-red text-xs">{renderError(t(formik.errors.taxcardnumber))}</div>
            )}

            <div className="w-[363px] md:w-full mt-2">
              <div className="rounded-lg border border-dashed border-primaryBlue px-2 py-3">
                <div className="flex flex-col gap-y-2" style={{ minHeight: 70, maxHeight: 220 }}>
                  {renderFileList("taxcardnumberurl", formik.values.taxcardnumberurl)}

                {formik.values.taxcardnumberurl?.length < 4 && (
  <div className={`flex flex-row text-sm leading-6 justify-center text-gray-600 ${
    formik.values.taxcardnumberurl?.length === 0 ? "w-full" : "w-8/12"
  }`}>
    <label
      htmlFor="file-upload-taxcardnumberurl"
      className={`flex items-center relative rounded-md font-semibold ${
        formik.values.taxcardnumberurl?.length >= 4
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-white text-primaryBlue cursor-pointer"
      }`}
    >
      <UploadBusinessMedia
        foldername={"store_app_UAT"}
        onfileAdd={"taxcardnumberurl"}
        urlgenerated={handleurlgenerated}
        multiple={true}
        message={() => false}
      />
    </label>
  </div>
)}

                </div>
              </div>
            </div>
          </div>

          {/* Manager ID */}
          <div>
            <InputBox
              label={t("managerid")}
              name="managerid"
              placeholder={t("enter_nationalid")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.managerid}
              error={
                (formik.values.manageridurl?.length > 0 &&
                  (formik.values.managerid ?? "").trim() === "") ||
                !!formik.errors.managerid
              }
              touched={
                (formik.values.manageridurl?.length > 0 &&
                  (formik.values.managerid ?? "").trim() === "") ||
                !!formik.touched.managerid
              }
            />
            {formik.errors.managerid && (
              <div className="text-red text-xs">{renderError(t(formik.errors.managerid))}</div>
            )}

            <div className="w-[363px] md:w-full mt-2">
              <div className="rounded-lg border border-dashed border-primaryBlue px-2 py-3">
                <div className="flex flex-col gap-y-2" style={{ minHeight: 70, maxHeight: 300 }}>
                  {renderFileList("manageridurl", formik.values.manageridurl)}

                  {formik.values.manageridurl?.length < 4 && (
  <div className={`flex flex-row text-sm leading-6 justify-center text-gray-600 ${
    formik.values.manageridurl?.length === 0 ? "w-full" : "w-8/12"
  }`}>
    <label
      htmlFor="file-upload-manageridurl"
      className={`flex items-center relative rounded-md font-semibold ${
        formik.values.manageridurl?.length >= 4
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-white text-primaryBlue cursor-pointer"
      }`}
    >
      <UploadBusinessMedia
        foldername={"store_app_UAT"}
        onfileAdd={"manageridurl"}
        urlgenerated={handleurlgenerated}
        multiple={true}
        message={() => false}
      />
    </label>
  </div>
)}

                </div>

                {formik.errors.manageridurl && (
                  <div className="text-red text-center justify-center text-xs">
                    {renderError(t(formik.errors.manageridurl))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className={`absolute end-10 bottom-10 w-[185px] h-[45px] ${isDisabled ? "bg-primaryBluedisabled" : "bg-primaryBlue"
            } text-white rounded-lg font-semibold float-right`}
          disabled={isDisabled}
        >
          {uploading ? t("Uploading...") : t("next")}
        </button>
      </form>

      {/* Image Viewer Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="relative">
            <img src={previewImage} alt="Preview" className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-xl" />

            <button onClick={() => setPreviewImage(null)} className="absolute top-2 right-2 bg-white px-3 py-1 rounded shadow">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* small uploading spinner overlay */}
      {uploading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-white p-4 rounded shadow flex items-center gap-3">
            <LoadingSpinner />
            <span>{t("Uploading files...")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessProof;



// import React from "react";
// import { useFormik } from "formik";
// import { InputBox } from "../CustomWidgets/InputBox";
// import UploadMedia from "../upload-media";
// import { BusinessProofSchema } from "../validationSchema";
// import { useTranslation } from "react-i18next";
// import { useSelector } from "react-redux";

// function BusinessProof({ onDataSubmit, savedata }) {
//   const { t } = useTranslation();
//   const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
//   const [previewImage, setPreviewImage] = React.useState(null);
  


//   const formik = useFormik({
//     initialValues: {
//       commercialregistrynumber: savedata ? savedata[0]?.name : "",
//       commercialregistrynumberurl: savedata ? savedata[0]?.url : "",

//       taxcardnumber: savedata ? savedata[1]?.name : "",
//       taxcardnumberurl: savedata ? savedata[1]?.url : "",

//       managerid: savedata ? savedata[2]?.name : "",
//       manageridurl: savedata ? savedata[2]?.url : "",
//     },
//     validationSchema: BusinessProofSchema,

//     onSubmit: (values) => {
//       const api_payload = {
//         user_id: user_id,
//         manager_id: values.managerid,
//         tax_card_number: values.taxcardnumber,
//         company_registry: values.commercialregistrynumber,
//       };
//       console.log("Submitting form data:", api_payload);
//       onDataSubmit("businessProofData", api_payload);
//     },
//   });

//   // const handleurlgenerated = (values) => {
//   //   formik.setFieldValue(values.name, values.url);


//   // };

//   const handleurlgenerated = (values) => {
//     formik.setFieldValue(values.name, values.url); // this is full base64
//     console.log("valuesvalues", values);

//   };

//   // const handleViewPDF = (base64) => {
//   //   if (base64) {
//   //     const byteCharacters = atob(base64);
//   //     const byteNumbers = Array.from(byteCharacters, (char) =>
//   //       char.charCodeAt(0)
//   //     );
//   //     const byteArray = new Uint8Array(byteNumbers);
//   //     const blob = new Blob([byteArray], { type: "application/pdf" });
//   //     const blobUrl = URL.createObjectURL(blob);
//   //     window.open(blobUrl, "_blank");
//   //   }
//   // };

//   const handleViewPDF = (base64) => {
//     const byteCharacters = atob(base64);
//     const byteNumbers = new Array(byteCharacters.length)
//       .fill()
//       .map((_, i) => byteCharacters.charCodeAt(i));

//     const byteArray = new Uint8Array(byteNumbers);
//     const blob = new Blob([byteArray], { type: "application/pdf" });

//     const blobUrl = URL.createObjectURL(blob);
//     window.open(blobUrl, "_blank");
//   };


//   const handleViewImage = (fullBase64Url) => {
//     setPreviewImage(fullBase64Url);   // open modal
//   }

//   const handleViewFile = (base64Full) => {
//     if (!base64Full) return;

//     const mimeType = base64Full.split(";")[0].replace("data:", "");

//     if (mimeType === "application/pdf") {
//       const pdfBlob = fetch(base64Full)
//         .then(res => res.blob())
//         .then(blob => {
//           const blobUrl = URL.createObjectURL(blob);
//           window.open(blobUrl, "_blank");
//         });
//     } else if (mimeType === "image/png" || mimeType === "image/jpeg") {
//       window.open(base64Full, "_blank");
//     } else {
//       console.error("Unsupported file type");
//     }
//   };


//   // Disable button if:
//   // - there are validation errors, OR
//   // - any URL exists but corresponding input text is empty
//   const isDisabled =
//     Object.keys(formik.errors).length > 0 ||
//     (formik.values.manageridurl !== "" && formik.values.managerid === "") ||
//     (formik.values.taxcardnumberurl !== "" && formik.values.taxcardnumber === "") ||
//     (formik.values.commercialregistrynumberurl !== "" &&
//       formik.values.commercialregistrynumber === "");

//   return (
//     <div>
//       <h1 className="text-xl font-bold">{t("business_proof")}</h1>
//       <form onSubmit={formik.handleSubmit}>
//         <div className="grid grid-cols-2 mt--4 text-sm gap-x-7 pb-12">
//           {/* Commercial Registry Number */}
//           <div>
//             <InputBox
//               label={t("commercial_registry_number")}
//               name="commercialregistrynumber"
//               placeholder={t("enter_commercial_registry_number")}
//               onChange={formik.handleChange}
//               onBlur={formik.handleBlur}
//               value={formik.values.commercialregistrynumber}
//               error={
//                 (formik.values.commercialregistrynumberurl !== "" &&
//                   formik.values.commercialregistrynumber === "") ||
//                 formik.errors.commercialregistrynumber
//               }
//               touched={
//                 (formik.values.commercialregistrynumberurl !== "" &&
//                   formik.values.commercialregistrynumber === "") ||
//                 formik.errors.commercialregistrynumber
//               }
//             />
//             {formik.errors.commercialregistrynumber && (
//               <div className="text-red text-xs">
//                 {t(formik.errors.commercialregistrynumber)}
//               </div>
//             )}
//             <div className="w-[363px] md:w-full mt-2">
//               <div className="rounded-lg border border-dashed border-primaryBlue px-2 py-3">
//                 <div
//                   className="flex flex-row justify-start gap-x-4"
//                   style={{ minHeight: 70, maxHeight: 70 }}
//                 >
//                   {formik.values?.commercialregistrynumberurl !== "" && (
//                     <button
//                       type="button"
//                       onClick={() =>
//                         handleViewPDF(formik.values?.commercialregistrynumberurl)
//                       }
//                       className="text-blue-500 underline mt-2 ms-6 flex items-center flex-col w-20"
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         className="h-6 w-6 mt-2"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M12 8v4l3 3m-3-3H8m4-4V4m0 0H4a2 2 0 00-2 2v16a2 2 0 002 2h16a2 2 0 002-2V8l-6-6H8z"
//                         />
//                       </svg>
//                       <span className="text-sm">{t("View File")}</span>
//                     </button>
//                   )}

//                   <div
//                     className={`flex flex-row text-sm leading-6 justify-center text-gray-600 ${formik.values?.commercialregistrynumberurl === ""
//                       ? "w-full"
//                       : " w-8/12"
//                       }`}
//                   >
//                     <label
//                       htmlFor="file-upload-commercialregistrynumberurl"
//                       className="flex items-center relative cursor-pointer rounded-md bg-white font-semibold text-primaryBlue focus-within:outline-none focus-within:ring-2 focus-within:ring-white focus-within:ring-offset-2 hover:text-primaryBlue"
//                     >
//                       <UploadMedia
//                         foldername={"store_app_UAT"}
//                         onfileAdd={"commercialregistrynumberurl"}
//                         urlgenerated={handleurlgenerated}
//                         imageurl={formik.values.commercialregistrynumberurl}
//                         message={() => false}
//                       />
//                     </label>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Tax Card Number */}
//           <div>
//             <InputBox
//               label={t("taxcard_number")}
//               name="taxcardnumber"
//               placeholder={t("enter_taxcard_number")}
//               onChange={formik.handleChange}
//               onBlur={formik.handleBlur}
//               value={formik.values.taxcardnumber}
//               error={
//                 (formik.values.taxcardnumberurl !== "" &&
//                   formik.values.taxcardnumber === "") ||
//                 formik.errors.taxcardnumber
//               }
//               touched={
//                 (formik.values.taxcardnumberurl !== "" &&
//                   formik.values.taxcardnumber === "") ||
//                 formik.errors.taxcardnumber
//               }
//             />
//             {formik.errors.taxcardnumber && (
//               <div className="text-red text-xs">{t(formik.errors.taxcardnumber)}</div>
//             )}
//             <div className="w-[363px] md:w-full mt-2">
//               <div className="rounded-lg border border-dashed border-primaryBlue px-2 py-3">
//                 <div
//                   className="flex flex-row justify-start gap-x-4"
//                   style={{ minHeight: 70, maxHeight: 70 }}
//                 >
//                   {formik.values?.taxcardnumberurl !== "" && (
//                     <button
//                       type="button"
//                       onClick={() => handleViewPDF(formik.values?.taxcardnumberurl)}
//                       className="text-blue-500 underline mt-2 ms-6 flex items-center flex-col w-20"
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         className="h-6 w-6 mt-2"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M12 8v4l3 3m-3-3H8m4-4V4m0 0H4a2 2 0 00-2 2v16a2 2 0 002 2h16a2 2 0 002-2V8l-6-6H8z"
//                         />
//                       </svg>
//                       <span className="text-sm">{t("View File")}</span>
//                     </button>
//                   )}

//                   <div
//                     className={`flex flex-row text-sm leading-6 justify-center text-gray-600 ${formik.values?.taxcardnumberurl === "" ? "w-full" : " w-8/12"
//                       }`}
//                   >
//                     <label
//                       htmlFor="file-upload-taxcardnumberurl"
//                       className="flex items-center relative cursor-pointer rounded-md bg-white font-semibold text-primaryBlue focus-within:outline-none focus-within:ring-2 focus-within:ring-white focus-within:ring-offset-2 hover:text-primaryBlue"
//                     >
//                       <UploadMedia
//                         foldername={"store_app_UAT"}
//                         onfileAdd={"taxcardnumberurl"}
//                         urlgenerated={handleurlgenerated}
//                         imageurl={formik.values.taxcardnumberurl}
//                         message={() => false}
//                       />
//                     </label>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Manager ID */}
//           <div>
//             <InputBox
//               label={t("managerid")}
//               name="managerid"
//               placeholder={t("enter_nationalid")}
//               onChange={formik.handleChange}
//               onBlur={formik.handleBlur}
//               value={formik.values.managerid}
//               error={
//                 (formik.values.manageridurl !== "" &&
//                   formik.values.managerid === "") ||
//                 formik.errors.managerid
//               }
//               touched={
//                 (formik.values.manageridurl !== "" &&
//                   formik.values.managerid === "") ||
//                 formik.errors.managerid
//               }
//             />
//             {formik.errors.managerid && (
//               <div className="text-red text-xs">{t(formik.errors.managerid)}</div>
//             )}
//             <div className="w-[363px] md:w-full mt-2">
//               <div className="rounded-lg border border-dashed border-primaryBlue px-2 py-3">
//                 <div
//                   className="flex flex-row justify-start gap-x-4"
//                   style={{ minHeight: 70, maxHeight: 70 }}
//                 >
//                   {/* {formik.values?.manageridurl !== "" && (
//                     <button
//                       type="button"
//                       onClick={() => handleViewPDF(formik.values?.manageridurl)}
//                       className="text-blue-500 underline mt-2 ms-6 flex items-center flex-col w-20"
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         className="h-6 w-6 mt-2"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M12 8v4l3 3m-3-3H8m4-4V4m0 0H4a2 2 0 00-2 2v16a2 2 0 002 2h16a2 2 0 002-2V8l-6-6H8z"
//                         />
//                       </svg>
//                       <span className="text-sm">{t("View File")}</span>
//                     </button>
//                   )} */}

//                   {formik.values?.manageridurl && (
//                     <button
//                       type="button"
//                       onClick={() => {
//                         const fileUrl = formik.values.manageridurl;

//                         if (fileUrl.startsWith("data:application/pdf")) {
//                           const base64 = fileUrl.split(",")[1];
//                           handleViewPDF(base64);
//                         } else if (
//                           fileUrl.startsWith("data:image/jpeg") ||
//                           fileUrl.startsWith("data:image/png")
//                         ) {
//                           handleViewImage(fileUrl); // open modal
//                         } else {
//                           console.error("Unsupported file type");
//                         }
//                       }}
//                       className="text-blue-500 underline mt-2 ms-6 flex items-center flex-col w-20"
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         className="h-6 w-6 mt-2"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M12 8v4l3 3m-3-3H8m4-4V4m0 0H4a2 2 0 00-2 2v16a2 2 0 002 2h16a2 2 0 002-2V8l-6-6H8z"
//                         />
//                       </svg>
//                       <span className="text-sm">{t("View File")}</span>
//                     </button>
//                   )}

//                   <div
//                     className={`flex flex-row text-sm leading-6 justify-center text-gray-600 ${formik.values?.manageridurl === "" ? "w-full" : " w-8/12"
//                       }`}
//                   >
//                     <label
//                       htmlFor="file-upload-manageridurl"
//                       className="flex items-center relative cursor-pointer rounded-md bg-white font-semibold text-primaryBlue focus-within:outline-none focus-within:ring-2 focus-within:ring-white focus-within:ring-offset-2 hover:text-primaryBlue"
//                     >
//                       <UploadMedia
//                         foldername={"store_app_UAT"}
//                         onfileAdd={"manageridurl"}
//                         urlgenerated={handleurlgenerated}
//                         imageurl={formik.values.manageridurl}
//                         message={() => false}
//                       />
//                     </label>
//                   </div>

//                 </div>
//                 {formik.errors.manageridurl && (
//                   <div className="text-red text-center justify-center text-xs">{t(formik.errors.manageridurl)}</div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>

//         <button
//           type="submit"
//           className={`absolute end-10 bottom-10 w-[185px] h-[45px] ${isDisabled ? "bg-primaryBluedisabled" : "bg-primaryBlue"
//             } text-white rounded-lg font-semibold float-right`}
//           disabled={isDisabled}
//         >
//           {t("next")}
//         </button>
//       </form>

//       {previewImage && (
//         <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
//           <div className="relative">
//             <img
//               src={previewImage}
//               alt="Preview"
//               className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-xl"
//             />

//             <button
//               onClick={() => setPreviewImage(null)}
//               className="absolute top-2 right-2 bg-white px-3 py-1 rounded shadow"
//             >
//               ✕
//             </button>
//           </div>
//         </div>
//       )}
//     </div>

//   );
// }

// export default BusinessProof;

